// Loads coordinator notes (and their replies) for many patients, for the
// Query RegX "Notes contains" filter.
//
// Notes live per patient at patient_notes/{patientId}/notes, replies under
// each note. There is no collection-group read: the rules don't allow one and
// the notes carry no site stamp, so each loaded patient's timeline is read
// through the same per-patient rules the patient page uses. Only called when a
// notes search is active; the page caches the result for the visit.
//
// Failures are collected per patient, never thrown, so one unreadable
// timeline can't blank the whole search — the page reports how many failed.

import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { getPatientNotesEntriesPath, getPatientNoteRepliesPath } from "./firestorePaths";

const toDate = (v) => (v && typeof v.toDate === "function" ? v.toDate() : v || null);

async function loadOne(patientId) {
  const notesSnap = await getDocs(collection(db, getPatientNotesEntriesPath(patientId)));
  const entries = [];
  for (const n of notesSnap.docs) {
    const note = n.data();
    entries.push({ kind: "note", body: note.body || "", createdAt: toDate(note.createdAt) });
    const repliesSnap = await getDocs(collection(db, getPatientNoteRepliesPath(patientId, n.id)));
    repliesSnap.docs.forEach((r) => {
      const reply = r.data();
      entries.push({ kind: "reply", body: reply.body || "", createdAt: toDate(reply.createdAt) });
    });
  }
  return entries;
}

/**
 * @param {string[]} patientIds
 * @param {{concurrency?: number, onProgress?: (done: number, total: number) => void}} [opts]
 * @returns {Promise<{byPatient: Map<string, Array>, failed: string[]}>}
 */
export async function loadCoordinatorNotes(patientIds, { concurrency = 20, onProgress } = {}) {
  const ids = Array.from(new Set((patientIds || []).filter(Boolean)));
  const byPatient = new Map();
  const failed = [];
  let next = 0;
  let done = 0;

  const worker = async () => {
    while (next < ids.length) {
      const id = ids[next];
      next += 1;
      try {
        byPatient.set(id, await loadOne(id));
      } catch (err) {
        console.error("Couldn't read coordinator notes for", id, err);
        failed.push(id);
      }
      done += 1;
      if (onProgress) onProgress(done, ids.length);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, ids.length) }, worker));
  return { byPatient, failed };
}
